/**
 * Buyer Waiting State Component
 * Shown after buyer submits contract but before agent/seller responds
 */

import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Mail,
  MessageSquare,
  Phone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { daysUntil, parseDateOnly } from '@/utils/dateOnly';
import { productAddressLine } from '@/utils/productAddress';
import { Contract } from '@/types/ContractType';
import { ContractVoidedNotification } from './ContractVoidedNotification';
import { VoidContractButton } from './VoidContractButton';

interface BuyerWaitingStateProps {
  contract: Contract;
  contractId: string;
  onSendMessage?: (message: string) => Promise<void>;
}

export const BuyerWaitingState: React.FC<BuyerWaitingStateProps> = ({
  contract,
  contractId,
  onSendMessage,
}) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [daysUntilExpiration, setDaysUntilExpiration] = useState<number | null>(
    null
  );

  // Calculate days until offer expiration
  useEffect(() => {
    setDaysUntilExpiration(daysUntil(contract?.timeline?.offerGoodUntillDate));
  }, [contract]);

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsSending(true);
    try {
      if (onSendMessage) {
        await onSendMessage(message);
      }
      toast.success('Message sent to agent!');
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Get agent contact info
  const agentInfo = contract?.agentInfo;
  // Priority: seller overrides > product database (source of truth) > legacy agentSellerInfo
  const propertyAddress =
    contract?.sellerPropertyInfo?.propertyAddress ||
    contract?.product?.address?.fullAddress ||
    contract?.product?.address?.street ||
    productAddressLine(contract?.product) ||
    contract?.agentSellerInfo?.propertyAddress ||
    'your property';

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Show rejection notification if contract is voided */}
      {contract?.contractStatus === 'voided' && (
        <ContractVoidedNotification contract={contract} />
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2 tracking-tight">
          Offer Submitted - Awaiting Response
        </h1>
        <p className="text-muted-foreground text-base leading-relaxed">
          Your offer for{' '}
          <span className="font-semibold text-foreground">
            {propertyAddress}
          </span>{' '}
          has been submitted successfully.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Status & Timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Actions */}
          <Card className="border border-[var(--outline-variant)]">
            <CardHeader>
              <CardTitle className="text-foreground">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="secondary"
                className="w-full justify-start h-10 px-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--on-surface)_10%,transparent)] hover:ring-[color-mix(in_srgb,var(--on-surface)_18%,transparent)]"
                onClick={() => navigate(`/view-contract/${contractId}`)}
              >
                <FileText className="h-4 w-4" />
                View Your Contract
              </Button>
              <Button
                variant="secondary"
                className="w-full justify-start h-10 px-4 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--on-surface)_10%,transparent)] hover:ring-[color-mix(in_srgb,var(--on-surface)_18%,transparent)]"
                onClick={() => navigate(`/contract/download/${contractId}`)}
              >
                <Download className="h-4 w-4" />
                Download PDF Contract
              </Button>

              {/* Withdraw Offer Button */}
              {contract?.contractStatus !== 'voided' &&
                contract?.contractStatus !== 'ratified' && (
                  <VoidContractButton
                    contractId={contractId}
                    voidedBy="buyer"
                    buttonText="Withdraw Offer"
                    variant="outline"
                    className="w-full justify-start h-10 px-4 text-destructive ring-1 ring-[color-mix(in_srgb,var(--destructive)_35%,transparent)] hover:bg-[color-mix(in_srgb,var(--destructive)_8%,transparent)]"
                    onVoidSuccess={() => {
                      window.location.href = '/properties';
                    }}
                  />
                )}

              {/* Message Agent Card */}
              <Card className="border border-[var(--outline-variant)] bg-muted/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                    <MessageSquare className="h-5 w-5 shrink-0 text-primary" />
                    Message Agent
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label htmlFor="agent-message" className="sr-only">
                      Message to listing agent
                    </label>
                    <Textarea
                      id="agent-message"
                      placeholder="e.g., I'd like to schedule a showing or discuss the offer timeline..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={5}
                      className="w-full"
                    />
                  </div>
                  <Button
                    variant="default"
                    onClick={handleSendMessage}
                    disabled={!message.trim() || isSending}
                    className="w-full h-10"
                  >
                    {isSending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[var(--primary-foreground)] border-t-transparent opacity-90" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    Messages are sent securely through the Unbeatable platform
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
          {/* Offer Status Card */}
          <Card className="border-blue-500 bg-gradient-to-br from-blue-50 to-white">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-blue-800 flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6" />
                Offer Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Offer Submitted</p>
                  <p className="text-sm text-gray-600">
                    Your offer was submitted on{' '}
                    {contract?.createdAt
                      ? new Date(
                          (contract as any).createdAt
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-yellow-100 p-2 rounded-full animate-pulse">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    Awaiting Agent Review
                  </p>
                  <p className="text-sm text-gray-600">
                    The listing agent will review your offer and coordinate with
                    the seller. This typically takes 24-48 hours.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 opacity-50">
                <div className="bg-gray-100 p-2 rounded-full">
                  <MessageSquare className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-500">Seller Response</p>
                  <p className="text-sm text-gray-500">
                    The seller will accept, reject, or counter your offer
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Agent Contact & Messaging */}
        <div className="space-y-6">
          {/* Agent Contact Card */}
          {agentInfo && (
            <Card className="border-gray-300">
              <CardHeader>
                <CardTitle className="text-lg">Listing Agent Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-black">Agent Name</p>
                  <p className="font-semibold text-black">
                    {agentInfo.name || 'Not Available'}
                  </p>
                </div>

                {agentInfo.sellerAgentEmail && (
                  <div>
                    <p className="text-sm text-black mb-1">Email</p>
                    <a
                      href={`mailto:${agentInfo.sellerAgentEmail}`}
                      className="flex items-center gap-2 text-black hover:text-blue-800 transition-colors"
                    >
                      <Mail className="h-4 w-4" />
                      <span className="text-sm">
                        {agentInfo.sellerAgentEmail}
                      </span>
                    </a>
                  </div>
                )}

                {agentInfo.sellerPhoneNumber && (
                  <div>
                    <p className="text-sm text-black mb-1">Phone</p>
                    <a
                      href={`tel:${agentInfo.sellerPhoneNumber}`}
                      className="flex items-center gap-2 text-black hover:text-blue-800 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      <span className="text-sm">
                        {agentInfo.sellerPhoneNumber}
                      </span>
                    </a>
                  </div>
                )}

                {agentInfo.agentCompanyName && (
                  <div>
                    <p className="text-sm text-black">Brokerage</p>
                    <p className="font-medium text-gray-900">
                      {agentInfo.agentCompanyName}
                    </p>
                  </div>
                )}

                {agentInfo.agentLicenseNumber && (
                  <div>
                    <p className="text-sm text-black">License #</p>
                    <p className="text-sm text-black">
                      {agentInfo.agentLicenseNumber}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Offer Expiration Card */}
          {daysUntilExpiration !== null && (
            <Card
              className={
                daysUntilExpiration <= 2 ? 'border-red-500' : 'border-gray-200'
              }
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock
                    className={`h-5 w-5 ${
                      daysUntilExpiration <= 2
                        ? 'text-red-600'
                        : 'text-gray-600'
                    }`}
                  />
                  Offer Expiration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div
                    className={`text-5xl font-bold mb-2 ${
                      daysUntilExpiration <= 0
                        ? 'text-red-600'
                        : daysUntilExpiration <= 2
                        ? 'text-orange-600'
                        : 'text-blue-600'
                    }`}
                  >
                    {daysUntilExpiration}
                  </div>
                  <p className="text-gray-600">
                    {daysUntilExpiration === 1 ? 'Day' : 'Days'} Remaining
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Offer expires on{' '}
                    {parseDateOnly(
                      contract?.timeline?.offerGoodUntillDate
                    )?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                  {daysUntilExpiration <= 2 && daysUntilExpiration > 0 && (
                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-800 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Offer expiring soon! Consider reaching out to the agent.
                      </p>
                    </div>
                  )}
                  {daysUntilExpiration <= 0 && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Offer has expired. You may need to submit a new offer.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* What Happens Next Section */}
      <Card className="mt-6 border-gray-200">
        <CardHeader>
          <CardTitle>What Happens Next?</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-gray-700">
            <li className="flex gap-2">
              <span className="font-semibold text-blue-700 shrink-0">1.</span>{' '}
              Agent reviews and presents your offer to the seller (typically
              24–48 hrs).
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-blue-700 shrink-0">2.</span>{' '}
              Seller accepts, rejects, or proposes counter-terms — you'll be
              notified.
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-green-700 shrink-0">3.</span>{' '}
              Once all terms are agreed, both parties sign to finalize the
              contract.
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default BuyerWaitingState;
